import React, { useState } from "react";
import posthog from "posthog-js";
import { TYPES } from "../../../constants/wineTypes";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import { Pill } from "../../../components/ui/Pill";
import { PublicOrderAccess, submitOrder } from "./orderForm.api";

interface Wine {
  id: string;
  type: string;
  name: string;
  mil: string;
  dom: string;
  prix: string;
}

interface OrderFormModalProps {
  wines: Wine[];
  maison: string;
  onClose: () => void;
  publicOrderAccess?: PublicOrderAccess;
  cart?: Record<string, number>;
  onUpdateQty?: (wineId: string, delta: number) => void;
  onCartContinue?: (payload: { distinctWineCount: number; bottleCount: number; total: number }) => void;
  onBackToCart?: (payload: { distinctWineCount: number; bottleCount: number; total: number }) => void;
  onOrderSubmitted?: (payload: { distinctWineCount: number; bottleCount: number; total: number }) => void;
  onOrderSubmitFailed?: (payload: { distinctWineCount: number; bottleCount: number; total: number; message: string }) => void;
}

export function OrderFormModal({
  wines,
  maison,
  onClose,
  publicOrderAccess,
  cart: controlledCart,
  onUpdateQty,
  onCartContinue,
  onBackToCart,
  onOrderSubmitted,
  onOrderSubmitFailed,
}: OrderFormModalProps) {
  const [internalCart, setInternalCart] = useState<Record<string, number>>(() =>
    wines.reduce((a, w) => ({ ...a, [w.id]: 0 }), {} as Record<string, number>)
  );
  const [form, setForm] = useState({
    nom: "", email: "", tel: "", adresse: "", societe: "", message: "", dateSouhaitee: "",
  });
  const [step, setStep] = useState<"cart" | "form" | "sent">("cart");
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cart = controlledCart ?? internalCart;

  const cartItems = wines.filter((w) => cart[w.id] > 0);
  const total = cartItems.reduce((s, w) => s + (parseFloat(w.prix) || 0) * cart[w.id], 0);
  const cartSummary = {
    distinctWineCount: cartItems.length,
    bottleCount: cartItems.reduce((sum, wine) => sum + cart[wine.id], 0),
    total,
  };

  const missingRequiredFields: string[] = [];
  if (!form.nom.trim()) {
    missingRequiredFields.push("le nom complet");
  }
  if (!form.email.trim()) {
    missingRequiredFields.push("l'email");
  }
  if (!form.tel.trim()) {
    missingRequiredFields.push("le telephone");
  }

  const updateQty = (id: string, delta: number) => {
    if (onUpdateQty) {
      onUpdateQty(id, delta);
      return;
    }

    setInternalCart((currentCart) => ({
      ...currentCart,
      [id]: Math.max(0, (currentCart[id] || 0) + delta),
    }));
  };

  const sendOrder = async () => {
    const items = cartItems.map((w) => ({ wineId: w.id, quantity: cart[w.id] }));
    try {
      setSubmitting(true);
      setError(null);
      const result = await submitOrder(items, form, maison, publicOrderAccess);
      posthog.capture("order_submitted", {
        distinct_wine_count: cartSummary.distinctWineCount,
        bottle_count: cartSummary.bottleCount,
        order_value: cartSummary.total,
        currency: "EUR",
        access_mode: publicOrderAccess?.userId ? "scoped" : "hash",
        has_custom_carte_id: Boolean(publicOrderAccess?.carteId && publicOrderAccess.carteId !== "default"),
      });
      setPreview(result.orderText);
      onOrderSubmitted?.(cartSummary);
      setStep("sent");
    } catch (e: any) {
      const message = e.message || "Erreur lors de l'envoi de la commande.";
      onOrderSubmitFailed?.({
        ...cartSummary,
        message,
      });
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (missingRequiredFields.length > 0) {
      setError(`Veuillez renseigner ${missingRequiredFields.join(", ")}.`);
      return;
    }

    await sendOrder();
  };

  return (
    <Modal onClose={onClose} title="Bon de commande" wide>
      {step === "cart" && (
        <>
          <div className="text-[12px] text-secondary mb-3">Sélectionnez vos vins et quantités :</div>
          <div className="max-h-[280px] overflow-y-auto mb-3 border border-border rounded-md">
            {wines.map((w, i) => (
              <div key={w.id} className={`flex items-center gap-2 px-3.5 py-2.5 ${i < wines.length - 1 ? "border-b border-foreground/[0.04]" : ""}`}>
                <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: TYPES.find((t) => t.id === w.type)?.color || "currentColor" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-light text-foreground overflow-hidden text-ellipsis whitespace-nowrap">{w.name} <span className="text-tertiary text-[11px]">{w.mil}</span></div>
                  <div className="text-[10px] text-tertiary">{w.dom}</div>
                </div>
                <div className="text-[12px] text-gold shrink-0 w-10 text-right">{w.prix}€</div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => updateQty(w.id, -1)} className={`w-8 h-8 rounded-md flex items-center justify-center cursor-pointer text-base font-sans ${cart[w.id] > 0 ? "bg-error/[0.08] border border-error/20 text-error" : "bg-transparent border border-border text-tertiary"}`}>−</button>
                  <span className={`w-6 text-center text-sm font-medium ${cart[w.id] > 0 ? "text-foreground" : "text-tertiary"}`}>{cart[w.id] || 0}</span>
                  <button type="button" onClick={() => updateQty(w.id, 1)} className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer btn-accent text-base font-sans">+</button>
                </div>
              </div>
            ))}
          </div>
          {cartItems.length > 0 && (
            <div className="flex justify-between items-center px-3.5 py-2.5 bg-accent/[0.05] border border-accent/15 rounded-md mb-3">
              <span className="text-[13px] text-secondary">{cartItems.length} réf. · {cartItems.reduce((s, w) => s + cart[w.id], 0)} bout.</span>
              <span className="text-base font-medium text-gold">{total.toFixed(2)} €</span>
            </div>
          )}
          <Button
            type="button"
            variant="accent"
            className="w-full justify-center"
            disabled={cartItems.length === 0}
            onClick={() => {
              onCartContinue?.(cartSummary);
              setStep("form");
            }}
          >
            Continuer →
          </Button>
        </>
      )}

      {step === "form" && (
        <form onSubmit={handleFormSubmit} className="space-y-3">
          <button
            type="button"
            onClick={() => {
              onBackToCart?.(cartSummary);
              setStep("cart");
            }}
            className="text-[12px] text-secondary bg-transparent border-none cursor-pointer font-sans mb-2 p-0"
          >
            ← Retour au panier
          </button>
          <div className="grid grid-cols-2 gap-x-3.5">
            <div className="col-span-full"><label className="label">Nom complet *</label><input value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} placeholder="Jean Dupont" className="input" /></div>
            <div><label className="label">Email *</label><input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jean@email.com" className="input" /></div>
            <div><label className="label">Téléphone *</label><input type="tel" value={form.tel} onChange={(e) => setForm((f) => ({ ...f, tel: e.target.value }))} placeholder="06 12 34 56 78" className="input" /></div>
            <div><label className="label">Société</label><input value={form.societe} onChange={(e) => setForm((f) => ({ ...f, societe: e.target.value }))} placeholder="Optionnel" className="input" /></div>
            <div><label className="label">Date souhaitée</label><input type="date" value={form.dateSouhaitee} onChange={(e) => setForm((f) => ({ ...f, dateSouhaitee: e.target.value }))} className="input" /></div>
            <div className="col-span-full"><label className="label">Adresse de livraison</label><input value={form.adresse} onChange={(e) => setForm((f) => ({ ...f, adresse: e.target.value }))} placeholder="12 rue de la Vigne, 21000 Dijon" className="input" /></div>
            <div className="col-span-full"><label className="label">Message</label><textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={3} placeholder="Précisions…" className="input resize-y leading-[1.6]" /></div>
          </div>
          <div className="mt-3.5 px-3.5 py-2.5 bg-accent/[0.05] border border-accent/15 rounded-md mb-3">
            <div className="text-[11px] tracking-[1.5px] text-tertiary uppercase mb-1.5">Récapitulatif</div>
            {cartItems.map((w) => (
              <div key={w.id} className="flex justify-between text-[12px] text-secondary py-0.5">
                <span>{cart[w.id]}x {w.name} {w.mil}</span>
                <span className="text-gold">{(cart[w.id] * parseFloat(w.prix || "0")).toFixed(2)}€</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-medium text-foreground mt-2 pt-2 border-t border-foreground/[0.08]">
              <span>Total</span><span className="text-gold">{total.toFixed(2)} €</span>
            </div>
          </div>
          {error && <div className="text-sm text-error">{error}</div>}
          <Button type="submit" variant="accent" className="w-full justify-center" disabled={submitting}>
            {submitting ? "Envoi en cours..." : "Envoyer la commande →"}
          </Button>
        </form>
      )}

      {step === "sent" && (
        <div className="py-2.5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-lg bg-success/10 border border-success/20 flex items-center justify-center shrink-0">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" className="fill-success/15" /><path d="M5.5 9.5L7.8 11.8L12.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <div>
              <div className="text-[15px] font-light text-foreground">Commande envoyée</div>
              <div className="text-[12px] text-secondary italic">L'établissement a été notifié</div>
            </div>
          </div>
          <div className="text-[11px] tracking-[1.5px] text-tertiary uppercase mb-1.5">Aperçu du bon</div>
          <pre className="bg-foreground/[0.02] border border-border rounded-md p-3.5 text-[11px] text-secondary leading-[1.7] max-h-[200px] overflow-y-auto whitespace-pre-wrap font-mono">{preview}</pre>
          <Pill active onClick={onClose} style={{ width: "100%", marginTop: 14 }}>Fermer</Pill>
        </div>
      )}
    </Modal>
  );
}
